import { crmApiUrl } from "./api"
import { tokenStore } from "../../lib/tokenStore.js"

/**
 * Release the originating Sales-agent lead(s) after their AI prospect(s) were
 * DELETED from the CRM — the reverse of "Convert to Prospect".
 *
 * Hits the DASHBOARD server, NOT crm-api: `/api/sales/*` lives on the dashboard
 * Fastify server (which holds the agent creds + does the `sales:edit` check),
 * whereas the CRM axios client talks to crm-api with the short-lived crm token.
 * So this goes straight through `fetch` with the DASHBOARD session token
 * (`tokenStore`). The server forwards to the agent's `POST /v2/leads/released`,
 * which clears each lead's exported / `crm_external_id` state and reverts it to
 * active so it reappears on the Sales Leads list.
 *
 * `crmExternalIds` are the deleted prospects' crm-api ids — the same value the
 * agent stored as `crm_external_id` on export, which is how the agent matches
 * the lead. Best-effort: returns `false` (never throws) on any failure, so a
 * release hiccup can never break the CRM delete that already succeeded.
 */
export async function releaseAiProspectLeads(crmExternalIds: string[]): Promise<boolean> {
  const ids = Array.from(
    new Set(crmExternalIds.map((s) => String(s ?? "").trim()).filter(Boolean)),
  )
  if (ids.length === 0) return false

  const access = tokenStore.getAccess()
  if (!access) return false

  try {
    const res = await fetch(crmApiUrl("/api/sales/v2/leads/released"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${access}` },
      credentials: "include",
      body: JSON.stringify({ crm_external_ids: ids }),
    })
    return res.ok
  } catch {
    return false
  }
}
